import React, { useEffect, useState } from 'react';
import AsyncSelect from 'react-select/async';
import { cachedFetch } from '../utils/cached-api-wrapper';
import Utils from '../utils/utils';

// Shared GL-segment picker for the option-backed segments (GL Section, Practice
// Group). Used by both the invoice header (invoice.js) and the per-line editor
// (additional-fields-modal.js) so the two render identically.

// Maps a stored value to a react-select option. Accepts the `{code, name}` object
// a user pick persists, a freshly-loaded `{Id, Description}` API row, and a bare
// code string — the shape the engine/api infer with no name (GLNatural-derived
// section, header->line cascade), which would otherwise render as a blank picker.
export const codeOptionMap = (stored) => {
    if (stored === null || stored === undefined) return null;
    const isScalar = typeof stored === 'string' || typeof stored === 'number';
    if (!isScalar && typeof stored !== 'object') return null;
    const rawCode = isScalar ? stored : (stored.code ?? stored.Id ?? stored.value ?? stored.GLValue);
    // Normalise scalar and object codes the same way (trimmed string) so the
    // rendered value and the resolver's String()-based match stay consistent.
    const code = (rawCode === null || rawCode === undefined || rawCode === '') ? null : String(rawCode).trim();
    if (!code) return null;
    // Some option sources already prefix their description with the code ("0001 - Firm");
    // the label adds it, so it is dropped here rather than shown twice.
    const rawDescription = isScalar ? undefined : (stored.name ?? stored.Description);
    const repeated = `${code} - `;
    const description = typeof rawDescription === 'string' && rawDescription.startsWith(repeated)
        ? rawDescription.slice(repeated.length) : rawDescription;
    return {
        value: { code, name: description },
        label: (
            <>
                <strong>{code}</strong>{description ? ` - ${description}` : ''}
            </>
        ),
    };
};

const makeClientOptionsLoader = (fieldName) => (search_query = '', callback = () => {}) => {
    const val = (search_query || '').trim().replace(/\//g, '');
    const filters = val.length > 0 ? [{ id: 'search', value: val }] : [];
    const searchData = `?field_name=${fieldName}&filters=${encodeURIComponent(JSON.stringify(filters))}`;
    cachedFetch(`/client/options/${searchData}`, 'GET', {}, (response) => {
        // An error envelope comes back as an object, not a list; treat anything that
        // is not a list as "no options" rather than throwing out of the render.
        const data = Array.isArray(response?.data) ? response.data : [];
        const rows = data.map(codeOptionMap).filter(Boolean);
        callback(rows.slice(0, 10));
    });
};

// The /client/options field_name differs from the invoice-json segment key
// (Practice Group is queried as `PracticeGroup`, not `GLPracticeGroup`).
const SEGMENT_LOADERS = {
    GLSection: makeClientOptionsLoader('GLSection'),
    GLPracticeGroup: makeClientOptionsLoader('PracticeGroup'),
    GLTimekeeper: makeClientOptionsLoader('GLTimekeeper'),
    GLOffice: makeClientOptionsLoader('GLOffice'),
    GLFacility: makeClientOptionsLoader('GLFacility'),
};

// A segment can arrive as a bare code string (engine/api inferred it with no
// name). When it does, look the code up in the field's options so the picker
// shows "code - name" like a user pick; fall back to code-only (codeOptionMap)
// if nothing matches. The lookup is a code-filtered fetch of its own (not the
// AsyncSelect's default list, which is capped at 10 rows and may not contain the
// inferred code) — correctness over reusing that request.
export const useResolvedSegmentOption = (stored, loader) => {
    const [resolved, setResolved] = useState(null);
    // A bare code can arrive as a string or (defensively) a number; normalise and
    // trim so a whitespace-only value is treated as empty, not a real code.
    const bareCode = (typeof stored === 'string' || typeof stored === 'number')
        ? (String(stored).trim() || null) : null;
    useEffect(() => {
        if (!bareCode || !loader) {
            setResolved(null);
            return undefined;
        }
        let cancelled = false;
        loader(bareCode, (rows) => {
            if (cancelled) return;
            setResolved((rows || []).find((r) => String(r?.value?.code) === bareCode) ?? null);
        });
        return () => { cancelled = true; };
    }, [bareCode, loader]);
    if (!bareCode) return codeOptionMap(stored);
    // Guard against a stale `resolved` from a previous code (line switch): only
    // use it when it matches the current code, else fall back until the fetch lands.
    const fresh = String(resolved?.value?.code) === bareCode ? resolved : null;
    return fresh ?? codeOptionMap(stored);
};

// `onChange` receives the persisted shape directly: the `{code, name}` object on
// a pick, or `undefined` when cleared — the same value the backend's
// _unwrap_segment_code already tolerates. Callers wire it to their own
// updateInvoice signature (header vs. per-line).
// `allowedCodes` (XPEN-3746) restricts the choices to those codes, e.g. the facilities
// that carry the line's account when a GL segment rule leaves the choice to the user.
// `compact` (a narrow line cell) drops the clear button and lets a long value wrap on whole
// words ("3000 - Health Care -" / "Litigation") rather than cutting it off.
const COMPACT_STYLES = {
    ...Utils.customStyleSelect,
    singleValue: (base) => ({
        ...Utils.customStyleSelect.singleValue(base),
        whiteSpace: 'normal',
        overflow: 'visible',
        textOverflow: 'clip',
        wordBreak: 'normal',
        overflowWrap: 'normal',
    }),
};

export { COMPACT_STYLES };

const GlSegmentSelect = ({ segment, value, onChange, isDisabled, name, placeholder, ariaLabel, required = false, allowedCodes = null, compact = false }) => {
    const baseLoader = SEGMENT_LOADERS[segment];
    const allowedKey = Array.isArray(allowedCodes) ? allowedCodes.join(',') : '';
    const loader = React.useMemo(() => {
        if (!baseLoader || !allowedKey) return baseLoader;
        const allowed = new Set(allowedKey.split(','));
        return (query, callback) => baseLoader(query, (rows) => callback((rows || []).filter((r) => allowed.has(String(r?.value?.code)))));
    }, [baseLoader, allowedKey]);
    const option = useResolvedSegmentOption(value, loader);
    if (!loader) return null; // unknown segment — nothing to load against
    return (
        <AsyncSelect
            // defaultOptions load once per mount: a new allowed set remounts so the list follows it.
            key={allowedKey || 'all'}
            styles={compact ? COMPACT_STYLES : Utils.customStyleSelect}
            menuPortalTarget={document.body}
            value={option}
            onChange={(choice) => onChange(choice ? choice.value : undefined)}
            classNamePrefix="select"
            isDisabled={isDisabled}
            required={required}
            name={name}
            // A placeholder is not an accessible name: a mount inside a labelled
            // table cell passes the column's label so the control is still named.
            aria-label={ariaLabel}
            placeholder={placeholder}
            isClearable={!compact}
            defaultOptions
            cacheOptions
            loadOptions={loader}
        />
    );
};

export default GlSegmentSelect;
